import { useMutation, useQuery } from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import { SET_WORKFLOW_ASSIGNMENT_MUTATION } from 'src/graphql/entities/workflow/workflow.mutations';
import { GET_WORKFLOW_ASSIGNMENTS_QUERY } from 'src/graphql/entities/workflow/workflow.operations';
import {
  isWorkflowTriggerType,
  WORKFLOW_TRIGGER_LABELS,
  WorkflowTriggerType,
} from 'src/graphql/entities/workflow/workflow.trigger';
import {
  GetWorkflowAssignmentsQuery,
  SetWorkflowAssignmentInput,
  SetWorkflowAssignmentMutation,
} from 'src/graphql/entities/workflow/workflow.types';
import { QueryVariables } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { useSnackbar } from 'src/utils/hooks/use-snackbar';

/** One partner's assignment for a trigger, as the assignments modal needs it. */
export interface WorkflowAssignmentItem {
  workflowAssignmentId: string;
  triggerType: WorkflowTriggerType;
  workflowId: string;
  /** Owner of the assigned workflow: the partner itself, or 0 for a platform workflow. */
  workflowPartnerId: number;
}

export type AssignmentsByTrigger = Partial<
  Record<WorkflowTriggerType, WorkflowAssignmentItem>
>;

export interface UseWorkflowAssignmentsResult {
  /**
   * Keyed by trigger. No entry means unassigned — it resolves through the platform default at
   * runtime, so absence is meaningful and must not be flattened into an empty string.
   */
  assignmentsByTrigger: AssignmentsByTrigger;
  loading: boolean;
  refetchAssignments: () => void;
}

/**
 * Reads the partner's own assignment rows, one per trigger at most. Scoped to the partner
 * alone: partner 0's rows are the runtime fallback, which the modal never displays.
 */
export function useWorkflowAssignments(
  partnerId?: number | null,
): UseWorkflowAssignmentsResult {
  const { showErrorAlert } = useNotifications();

  const { data, loading, refetch } = useQuery<GetWorkflowAssignmentsQuery, QueryVariables>(
    GET_WORKFLOW_ASSIGNMENTS_QUERY,
    {
      variables: isNullish(partnerId)
        ? undefined
        : {
            params: {
              filters: [{ attribute: 'partnerId', operator: '=', value: String(partnerId) }],
            },
          },
      skip: isNullish(partnerId),
      // The rows change from the page this opens over, so the cache is routinely stale.
      fetchPolicy: 'cache-and-network',
      onError: (fetchError) => {
        console.error(fetchError);
        showErrorAlert('Failed to load workflow assignments. Please try again.');
      },
    },
  );

  const assignmentsByTrigger = useMemo(() => {
    const own: AssignmentsByTrigger = {};

    (data?.getWorkflowAssignment?.records ?? []).forEach((record) => {
      // trigger_type is a schema String, so a row written outside the mutation can carry a
      // value this build does not know. Skip rather than render a blank row.
      if (!isWorkflowTriggerType(record.triggerType) || isNullish(record.workflowId)) return;

      own[record.triggerType] = {
        workflowAssignmentId: String(record.workflowAssignmentId),
        triggerType: record.triggerType,
        workflowId: String(record.workflowId),
        workflowPartnerId: Number(record.workflowPartnerId),
      };
    });

    return own;
  }, [data?.getWorkflowAssignment?.records]);

  const refetchAssignments = useCallback(() => {
    void refetch();
  }, [refetch]);

  return { assignmentsByTrigger, loading, refetchAssignments };
}

export interface SetWorkflowAssignmentArgs {
  partnerId: number;
  triggerType: WorkflowTriggerType;
  /** `null` clears the assignment, dropping the trigger back to the platform default. */
  workflowId: string | null;
}

export interface WorkflowAssignmentChange {
  workflowName: string;
  /** What the trigger ran before, `null` for the platform default; Undo puts it back. */
  previousWorkflowId: string | null;
}

export interface UseSetWorkflowAssignmentResult {
  /** Names the change in a toast with Undo, which restores `change.previousWorkflowId`. */
  setAssignment: (
    input: SetWorkflowAssignmentArgs,
    change: WorkflowAssignmentChange,
  ) => Promise<void>;
  /** Clears without the toast: automatic repair should not be announced as the user's save. */
  clearAssignment: (input: Omit<SetWorkflowAssignmentArgs, 'workflowId'>) => Promise<void>;
  saving: boolean;
}

export function useSetWorkflowAssignment(): UseSetWorkflowAssignmentResult {
  const { showErrorAlert, showSuccessAlert, showActionToast } = useSnackbar();
  const [mutate, { loading }] = useMutation<
    SetWorkflowAssignmentMutation,
    { input: SetWorkflowAssignmentInput }
  >(SET_WORKFLOW_ASSIGNMENT_MUTATION);

  const write = useCallback(
    async (input: SetWorkflowAssignmentArgs, refetchAssignments = false) => {
      try {
        await mutate({
          variables: { input },
          ...(refetchAssignments && { refetchQueries: [GET_WORKFLOW_ASSIGNMENTS_QUERY] }),
        });
      } catch (error) {
        console.error(error);
        // Wrong trigger type, no active version and another partner's are separate refusals
        // with different fixes, so surface the backend's message rather than a generic one.
        showErrorAlert(
          error instanceof Error && error.message
            ? error.message
            : 'Failed to save the workflow assignment. Please try again.',
        );
        throw error;
      }
    },
    [mutate, showErrorAlert],
  );

  const undoAssignment = useCallback(
    async (
      { partnerId, triggerType }: SetWorkflowAssignmentArgs,
      previousWorkflowId: string | null,
    ) => {
      try {
        await write({ partnerId, triggerType, workflowId: previousWorkflowId }, true);
        showSuccessAlert(`${WORKFLOW_TRIGGER_LABELS[triggerType]} assignment restored.`);
      } catch {
        // write already told the user why; there is no caller to rethrow to from a toast button.
      }
    },
    [write, showSuccessAlert],
  );

  const setAssignment = useCallback(
    async (
      input: SetWorkflowAssignmentArgs,
      { workflowName, previousWorkflowId }: WorkflowAssignmentChange,
    ) => {
      await write(input);
      showActionToast(
        `${WORKFLOW_TRIGGER_LABELS[input.triggerType]} now runs ${workflowName}`,
        {
          onUndo: () => void undoAssignment(input, previousWorkflowId),
        },
      );
    },
    [write, showActionToast, undoAssignment],
  );

  const clearAssignment = useCallback(
    (input: Omit<SetWorkflowAssignmentArgs, 'workflowId'>) =>
      write({ ...input, workflowId: null }),
    [write],
  );

  return { setAssignment, clearAssignment, saving: loading };
}
